import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowDownTrayIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import FarmSearchDropdown from '../components/FarmSearchDropdown';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmSelector from '../components/FarmSelector';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Reports = () => {
  const { farms, isAdmin, hasOrganizations, hasFarms } = useFarm();
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [activeTab, setActiveTab] = useState('financial');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [cycles, setCycles] = useState([]);
  const [financialData, setFinancialData] = useState({
    expensesCount: 0,
    expensesAmount: 0,
    incomeCount: 0,
    incomeAmount: 0,
    chartData: []
  });
  const [activitiesData, setActivitiesData] = useState({
    sopCompleted: 0,
    sopPending: 0,
    attendanceRate: 100,
    activities: []
  });
  const [harvestData, setHarvestData] = useState([]);
  const [inventoryData, setInventoryData] = useState({
    pesticide: [],
    fertilizer: [],
    mediaTreatment: []
  });

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Load all data
  useEffect(() => {
    const loadReportData = async () => {
      setLoading(true);
      try {
        // Get farm IDs for filtering
        let farmIds = [];
        if (selectedFarmFilter !== 'all') {
          farmIds = [parseInt(selectedFarmFilter)];
        } else {
          farmIds = farms.map(f => f.farm_id).filter(id => id);
        }

        // Load On-going Cycles from season_view (include null end_date as ongoing)
        let cyclesQuery = supabase
          .from('season_view')
          .select('season_id, season_name, farm_id, farm_name, name_produce, produce_icon, season_start_date, season_end_date');
        
        if (selectedFarmFilter !== 'all') {
          cyclesQuery = cyclesQuery.eq('farm_id', parseInt(selectedFarmFilter));
        }
        
        const { data: cyclesData } = await cyclesQuery.limit(50);
        
        // Filter for ongoing cycles (null end_date or end_date >= today)
        const today = new Date().toISOString().split('T')[0];
        const ongoingCycles = (cyclesData || []).filter(c => 
          !c.season_end_date || c.season_end_date >= today
        );
        setCycles(ongoingCycles.slice(0, 10));

        // Load Financial Data
        let finQuery = supabase
          .from('financial_transaction_view')
          .select('*')
          .gte('date_transaction', dateRange.start)
          .lte('date_transaction', dateRange.end);
        
        if (selectedFarmFilter !== 'all') {
          finQuery = finQuery.eq('farm', parseInt(selectedFarmFilter));
        }
        
        const { data: finData } = await finQuery.limit(1000);
        
        if (finData) {
          const expenses = finData.filter(f => f.in === false);
          const income = finData.filter(f => f.in === true);
          
          // Process chart data by month
          const monthlyData = {};
          finData.forEach(item => {
            const date = new Date(item.date_transaction);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = { month: monthKey, expenses: 0, income: 0 };
            }
            if (item.in === false) {
              monthlyData[monthKey].expenses += parseFloat(item.amount) || 0;
            } else {
              monthlyData[monthKey].income += parseFloat(item.amount) || 0;
            }
          });
          
          const chartData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
          
          setFinancialData({
            expensesCount: expenses.length,
            expensesAmount: expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
            incomeCount: income.length,
            incomeAmount: income.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0),
            chartData
          });
        }

        // Load Activities/SOP Data from sop_activity_farm_view
        let actQuery = supabase
          .from('sop_activity_farm_view')
          .select('activity_id, date_activity, process_name, status_name, farm_id, farm_name')
          .gte('date_activity', dateRange.start)
          .lte('date_activity', dateRange.end);
        
        if (selectedFarmFilter !== 'all') {
          actQuery = actQuery.eq('farm_id', parseInt(selectedFarmFilter));
        }
        
        const { data: actData } = await actQuery.limit(500);
        
        if (actData) {
          // Group by activity type (process_name)
          const activityCounts = {};
          actData.forEach(act => {
            const typeName = act.process_name || 'Other';
            if (!activityCounts[typeName]) {
              activityCounts[typeName] = { name: typeName, count: 0 };
            }
            activityCounts[typeName].count++;
          });
          
          setActivitiesData({
            sopCompleted: actData.filter(a => a.status_name === 'Pending').length,
            sopPending: actData.filter(a => a.status_name === 'Completed').length,
            attendanceRate: 100,
            activities: Object.values(activityCounts)
          });
        }

        // Load Harvest Data from harvest_view
        let harvQuery = supabase
          .from('harvest_view')
          .select('yield_transaction_id, yield_transaction_amount, yield_transaction_date, farm_id, farm_name, yield_produce_id, produce_name, produce_icon, unit_symbol')
          .gte('yield_transaction_date', dateRange.start)
          .lte('yield_transaction_date', dateRange.end)
          .eq('yield_in', true);
        
        if (selectedFarmFilter !== 'all') {
          harvQuery = harvQuery.eq('farm_id', parseInt(selectedFarmFilter));
        }
        
        const { data: harvData } = await harvQuery.limit(100);
        
        if (harvData) {
          // Group by produce name
          const harvestByProduce = {};
          harvData.forEach(h => {
            const produceName = h.produce_name || 'Unknown';
            if (!harvestByProduce[produceName]) {
              harvestByProduce[produceName] = { name: produceName, quantity: 0, image: h.produce_icon, unit: h.unit_symbol || 'kg' };
            }
            harvestByProduce[produceName].quantity += parseFloat(h.yield_transaction_amount) || 0;
          });
          setHarvestData(Object.values(harvestByProduce));
        }

        // Load Inventory Data from inventory_item_view (use 'in' filter for better RLS compatibility)
        let invQuery = supabase
          .from('inventory_item_view')
          .select('inventory_item_id, inventory_name, farm_id, material_type_name, net_inventory_movement, unit_symbol');
        
        if (selectedFarmFilter !== 'all') {
          invQuery = invQuery.in('farm_id', [parseInt(selectedFarmFilter)]);
        }
        
        const { data: invData } = await invQuery.limit(500);
        
        if (invData) {
          const filteredInv = invData;
          
          const pesticide = filteredInv.filter(i => i.material_type_name?.toLowerCase().includes('pesticide')).map(i => ({
            name: i.inventory_name,
            quantity: i.net_inventory_movement,
            unit: i.unit_symbol
          }));
          const fertilizer = filteredInv.filter(i => i.material_type_name?.toLowerCase().includes('fertilizer') || i.material_type_name?.toLowerCase().includes('fertiliser')).map(i => ({
            name: i.inventory_name,
            quantity: i.net_inventory_movement,
            unit: i.unit_symbol
          }));
          const mediaTreatment = filteredInv.filter(i => i.material_type_name?.toLowerCase().includes('media') || i.material_type_name?.toLowerCase().includes('treatment')).map(i => ({
            name: i.inventory_name,
            quantity: i.net_inventory_movement,
            unit: i.unit_symbol
          }));
          
          setInventoryData({ pesticide, fertilizer, mediaTreatment });
        }

      } catch (error) {
        console.error('Error loading report data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (farms.length > 0) {
      loadReportData();
    }
  }, [farms, selectedFarmFilter, dateRange]);

  // SOP completion data for pie chart
  const sopChartData = useMemo(() => {
    const total = activitiesData.sopCompleted + activitiesData.sopPending;
    return [
      { name: 'Completed', value: activitiesData.sopCompleted, color: '#22c55e' },
      { name: 'Pending', value: activitiesData.sopPending || (total === 0 ? 1 : 0), color: '#e5e7eb' }
    ];
  }, [activitiesData]);

  const sopPercentage = useMemo(() => {
    const total = activitiesData.sopCompleted + activitiesData.sopPending;
    return total > 0 ? Math.round((activitiesData.sopCompleted / total) * 100) : 0;
  }, [activitiesData]);

  // Activity color mapping
  const getActivityColor = (name) => {
    const colors = {
      'Pesticide': '#ef4444',
      'Harvest': '#22c55e',
      'Transplanting': '#3b82f6',
      'Flower Pollination': '#f97316',
      'Soil Loosening': '#eab308',
      'Seeding': '#8b5cf6',
      'Pruning': '#ec4899',
      'Open Crop Bed': '#14b8a6',
      'Fertilizer': '#84cc16',
      'Branch Opening': '#06b6d4',
      'Grafting': '#f59e0b'
    };
    return colors[name] || '#6b7280';
  };

  const handleDownloadReport = () => {
    alert('Report download feature coming soon!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Farm Report</h1>
        <div className="flex items-center space-x-4">
          {/* Organization Selector - Admin Only */}
          {hasOrganizations && <OrganizationSelector />}
          {/* Farm Selector */}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filters</label>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              placeholder="Search a farm..."
            />
          </div>
          <button
            onClick={handleDownloadReport}
            className="flex items-center gap-2 px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Download Report
          </button>
        </div>
      </div>

      {/* Report Title */}
      <div className="text-primary-600 font-medium">
        Farm Report from {formatDateDisplay(dateRange.start)} to {formatDateDisplay(dateRange.end)}
      </div>

      {/* On-going Cycles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">On-going Cycles</h2>
        {cycles.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {cycles.slice(0, 5).map((cycle, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {cycle.produce_icon && (
                  <img src={cycle.produce_icon} alt="" className="w-8 h-8 rounded-full object-cover" />
                )}
                <div>
                  <div className="text-sm text-gray-500">{cycles.length} Cycle(s)</div>
                  <div className="font-medium text-primary-600">{cycle.name_produce || cycle.season_name || 'Unknown'}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No active cycles found</p>
        )}
      </div>

      {/* Financial & Activities/SOP Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab('financial')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'financial' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Financial
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'activities' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Activities / SOP
            </button>
          </div>

          {activeTab === 'financial' && (
            <div className="space-y-6">
              {/* Expenses Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Expenses Activities</p>
                  <p className="text-3xl font-bold text-gray-900">{financialData.expensesCount}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Amount</p>
                  <p className="text-2xl font-bold text-red-600">
                    <span className="text-sm align-top">RM</span>
                    {financialData.expensesAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Income Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Income/Sales Activities</p>
                  <p className="text-3xl font-bold text-gray-900">{financialData.incomeCount}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Amount</p>
                  <p className="text-2xl font-bold text-green-600">
                    <span className="text-sm align-top">RM</span>
                    {financialData.incomeAmount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Chart */}
              {financialData.chartData.length > 0 && (
                <div className="h-64">
                  <div className="flex items-center gap-4 mb-2 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-red-400"></span> Expenses
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-400"></span> Income
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={financialData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => {
                          const [year, month] = val.split('-');
                          return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(val) => `RM${(val/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value) => [`RM${value.toLocaleString()}`, '']}
                        labelFormatter={(label) => {
                          const [year, month] = label.split('-');
                          return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        }}
                      />
                      <Area type="monotone" dataKey="income" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4">
              {/* Activity Counts Grid */}
              <div className="grid grid-cols-3 gap-2">
                {activitiesData.activities.slice(0, 12).map((activity, idx) => (
                  <div key={idx} className="text-center p-2">
                    <div 
                      className="w-full h-8 rounded flex items-center justify-center text-white text-xs font-medium mb-1"
                      style={{ backgroundColor: getActivityColor(activity.name) }}
                    >
                      {activity.name}
                    </div>
                    <div className="text-xs text-gray-500">x {activity.count} activities</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SOP & Attendance Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center gap-8">
            {/* Donut Chart */}
            <div className="relative">
              <PieChart width={200} height={200}>
                <Pie
                  data={sopChartData}
                  cx={100}
                  cy={100}
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {sopChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs text-gray-500">{100 - sopPercentage}% SOP Pending</span>
                <span className="text-sm font-medium text-gray-700">
                  {sopPercentage}% SOP Completed
                </span>
              </div>
            </div>

            {/* Attendance Rate */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Attendance Rate</p>
              <p className="text-4xl font-bold text-gray-900">{activitiesData.attendanceRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Harvest Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Harvest</h2>
        {harvestData.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {harvestData.map((harvest, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {harvest.image && (
                  <img src={harvest.image} alt="" className="w-8 h-8 rounded-full object-cover" />
                )}
                <div>
                  <div className="font-medium text-gray-900">{harvest.name}</div>
                  <div className="text-sm text-primary-600">{(harvest.quantity / 1000).toFixed(1)} t</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No harvest data found for the selected period</p>
        )}
      </div>

      {/* Inventory Level Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Inventory Level</h2>
        
        {/* Pesticide */}
        {inventoryData.pesticide.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 bg-gray-100 px-3 py-1 inline-block rounded">Pesticide</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {inventoryData.pesticide.slice(0, 8).map((item, idx) => (
                <div key={idx} className="text-sm">
                  <div className="font-medium text-primary-600 truncate">{item.name}</div>
                  <div className={`${parseFloat(item.quantity) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {parseFloat(item.quantity).toFixed(1)} {item.unit || 'kg'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fertilizer */}
        {inventoryData.fertilizer.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 bg-green-100 text-green-700 px-3 py-1 inline-block rounded">Fertilizer</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {inventoryData.fertilizer.slice(0, 8).map((item, idx) => (
                <div key={idx} className="text-sm">
                  <div className="font-medium text-primary-600 truncate">{item.name}</div>
                  <div className={`${parseFloat(item.quantity) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {parseFloat(item.quantity).toLocaleString()} {item.unit || 'kg'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media Treatment */}
        {inventoryData.mediaTreatment.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3 bg-blue-100 text-blue-700 px-3 py-1 inline-block rounded">Media Treatment</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {inventoryData.mediaTreatment.slice(0, 8).map((item, idx) => (
                <div key={idx} className="text-sm">
                  <div className="font-medium text-primary-600 truncate">{item.name}</div>
                  <div className={`${parseFloat(item.quantity) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {parseFloat(item.quantity).toLocaleString()} {item.unit || 'kg'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {inventoryData.pesticide.length === 0 && inventoryData.fertilizer.length === 0 && inventoryData.mediaTreatment.length === 0 && (
          <p className="text-gray-500">No inventory data found</p>
        )}
      </div>
    </div>
  );
};

export default Reports;
